import React, { useEffect, useState } from 'react';
import { FormattedDate } from 'react-intl';
import { gql } from '@apollo/client';
import { apolloClient } from '../../Root';
import { renderHideDeliveryTimes } from './utils/renderHideDeliveryTimes';
import SetByTimeInfo from './SetByTime';

// TD1-10482 (M4, PRD D-32): every stop the customer sees, from the storefront schema — label
// included, never recomputed here. The API owns the label rule (TD1-10504): a type with one stop is
// bare "Drop Off" / "Pick Up", a type with more numbers them D1..DN / P1..PN with the order's own
// stop prefixed, e.g. "First Drop Off D1" / "Last Pick Up P2". Read-only (D-15: no editing here).
//
// The schedule-derived blocks below stay hard-coded "Drop Off" / "Pick Up": that fallback is always
// exactly one stop of each type, which is what the rule produces for a single stop anyway.
const GET_STOREFRONT_RENTAL_DISPLAY_TRANSPORTS = gql`
  query GetStorefrontRentalDisplayTransports($rentalToken: String!) {
    getStorefrontRentalDisplayTransports(rentalToken: $rentalToken) {
      label
      transportType
      startDateTime
      endDateTime
      hasDeliveryWindow
      noPreference
    }
  }
`;

const Delivery = ({
  delivery,
  checkout,
  checkoutWindowEnd,
  checkout_time,
  checkinWindowStart,
  checkin_time,
  hideDeliveryTimes,
  hideDeliveryInfo,
  schedule,
  rental,
}) => {
  const timeZone = schedule.timeZone;
  const pickup = {
    pickup_same_as_delivery: rental.pickupSameAsDelivery,
    pickup_address_location_name: rental.pickupAddressLocationName,
    pickup_address_street_address_1: rental.pickupAddressStreetAddress1,
    pickup_address_street_address_2: rental.pickupAddressStreetAddress2,
    pickup_address_city: rental.pickupAddressCity,
    pickup_address_locale: rental.pickupAddressLocale,
    pickup_address_postal_code: rental.pickupAddressPostalCode,
    pickup_address_country: rental.pickupAddressCountry,
  };

  // null while loading, [] when the request failed, otherwise the API's list. The delivery times
  // are primary content, so nothing here is ever rendered as blank: until the stops arrive (and if
  // they never do) the schedule-derived Drop Off / Pick Up blocks are shown, and the stops replace
  // them once resolved. Before this query existed the times rendered synchronously from props, so
  // drawing nothing while it is in flight would flash empty on every load.
  const [stops, setStops] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (hideDeliveryInfo) {
      return undefined;
    }

    if (!rental.token) {
      setStops([]);
      return undefined;
    }

    apolloClient
      .query({
        query: GET_STOREFRONT_RENTAL_DISPLAY_TRANSPORTS,
        variables: { rentalToken: rental.token },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (cancelled) return;
        setStops(data?.getStorefrontRentalDisplayTransports || []);
      })
      .catch(() => {
        if (cancelled) return;
        setStops([]);
      });

    return () => {
      cancelled = true;
    };
  }, [rental.token, hideDeliveryInfo]);

  const renderDateLine = (value) => (
    <div>
      <FormattedDate
        month='2-digit'
        day='2-digit'
        year='2-digit'
        value={value}
        timeZone={timeZone}
      />
      {' - '}
      {renderHideDeliveryTimes(value, timeZone, hideDeliveryTimes)}
    </div>
  );

  // One stop's window with the same handling the Drop Off / Pick Up blocks have: "No Preference"
  // when the schedule says so, a single date/time for a point-in-time stop, start "Through" end
  // otherwise.
  const renderStopWindow = (stop) => (
    <div>
      {stop.noPreference ? (
        <div>No Preference</div>
      ) : (
        <div>
          {renderDateLine(stop.startDateTime)}
          {stop.hasDeliveryWindow && <br />}
        </div>
      )}
      {(stop.noPreference || stop.hasDeliveryWindow) && (
        <>
          <b>{' Through'}</b>
          <br />
          {stop.noPreference ? (
            <div>No Preference</div>
          ) : (
            renderDateLine(stop.endDateTime)
          )}
        </>
      )}
    </div>
  );

  const pickupAddress = !pickup.pickup_same_as_delivery && (
    <>
      <div>
        <h4>Pick Up Address</h4>
        <div>
          {pickup.pickup_address_location_name && (
            <strong>{pickup.pickup_address_location_name}</strong>
          )}
          <div>{pickup.pickup_address_street_address_1}</div>
          {pickup.pickup_address_street_address_2 && (
            <div># {pickup.pickup_address_street_address_2}</div>
          )}
          <div>
            {pickup.pickup_address_city && `${pickup.pickup_address_city}, `}
            {pickup.pickup_address_locale}{' '}
            {pickup.pickup_address_postal_code &&
              `${pickup.pickup_address_postal_code}, `}
            {pickup.pickup_address_country}
          </div>
        </div>
      </div>
      <br />
    </>
  );

  const firstPickUpIndex = (stops || []).findIndex(
    (stop) => stop.transportType === 'pick_up'
  );

  const renderStops = () => (
    <>
      {stops.map((stop, index) => (
        <div key={`${stop.transportType}-${stop.startDateTime}-${index}`}>
          {index === firstPickUpIndex && pickupAddress}
          <label>{stop.label}</label>
          {renderStopWindow(stop)}
        </div>
      ))}
      {/* The address block sits ahead of the first pick-up when there is one. Its visibility must
          depend only on `pickup_same_as_delivery` though — `findIndex` returns -1 for a stops list
          with no pick-up at all, which would otherwise drop the address silently. */}
      {firstPickUpIndex === -1 && pickupAddress}
    </>
  );

  const renderScheduleBlocks = () => (
    <>
      <div>
        <label>Drop Off</label>
        <div>
          {rental.schedule.eventStartSetting !== 'no_preference' ? (
            <div>
              <FormattedDate
                month='2-digit'
                day='2-digit'
                year='2-digit'
                value={checkout}
                timeZone={timeZone}
              />
              {' - '}
              {renderHideDeliveryTimes(
                checkout_time,
                timeZone,
                hideDeliveryTimes
              )}
              <br />
            </div>
          ) : (
            <div>No Preference</div>
          )}
          <b>{' Through'}</b>
          <br />
          {rental.schedule.eventStartSetting !== 'no_preference' ? (
            <div>
              <FormattedDate
                month='2-digit'
                day='2-digit'
                year='2-digit'
                value={checkoutWindowEnd}
                timeZone={timeZone}
              />
              {' - '}
              {renderHideDeliveryTimes(
                checkoutWindowEnd,
                timeZone,
                hideDeliveryTimes
              )}
            </div>
          ) : (
            <div>No Preference</div>
          )}
        </div>
      </div>
      <div>
        {pickupAddress}
        <label>Pick Up</label>
        <div>
          {rental.schedule.eventEndSetting !== 'no_preference' ? (
            <div>
              <FormattedDate
                month='2-digit'
                day='2-digit'
                year='2-digit'
                value={checkinWindowStart}
                timeZone={timeZone}
              />
              {' - '}
              {renderHideDeliveryTimes(
                checkinWindowStart,
                timeZone,
                hideDeliveryTimes
              )}
              <br />
            </div>
          ) : (
            <div>No Preference</div>
          )}
          <b>{' Through'}</b>
          <br />
          {rental.schedule.eventEndSetting !== 'no_preference' ? (
            <div>
              <FormattedDate
                month='2-digit'
                day='2-digit'
                year='2-digit'
                value={checkin_time}
                timeZone={timeZone}
              />
              {' - '}
              {renderHideDeliveryTimes(
                checkin_time,
                timeZone,
                hideDeliveryTimes
              )}
            </div>
          ) : (
            <div>No Preference</div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <section className='delivery'>
      <h4>Delivery Info</h4>
      {hideDeliveryInfo ? (
        <div className='details'>TBD</div>
      ) : (
        <div className='details'>
          <div>
            {delivery.delivery_address_location_name && (
              <label>{delivery.delivery_address_location_name}</label>
            )}
            <div>
              {delivery.delivery_address_street_address_1},{' '}
              {delivery.delivery_address_street_address_2 &&
                delivery.delivery_address_street_address_2}
            </div>

            <div>
              {delivery.delivery_address_city},{' '}
              {delivery.delivery_address_locale}{' '}
              {delivery.delivery_address_postal_code},{' '}
              {delivery.delivery_address_country}
            </div>
          </div>
          <br />
          {stops && stops.length > 0 ? renderStops() : renderScheduleBlocks()}
          {rental.hasSetByTime && rental.location.showSetByTimeToCustomers && (
            <SetByTimeInfo {...{ rental, schedule }} />
          )}
        </div>
      )}
    </section>
  );
};

export default Delivery;
